import { Button, Flex, Separator, Switch, Text } from '@radix-ui/themes'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import * as Collapsible from '@radix-ui/react-collapsible'
import { LuChevronRight } from 'react-icons/lu'
import cn from 'classnames'
import OrgMemberPicker from '~/features/orgnisations/components/OrgMemberPicker'
import { SectionCard } from '~/components/SectionCard'
import { useTopicSubscribersState } from '~/features/notifications/hooks/useTopicSubscribersState'
import { SiteSchema } from '../schemas'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateSite } from '../service'
import { toast } from 'sonner'
import { useFeatureEnabled } from '~/features/internal-analytics/useFeatureEnabled'
import { TRPCInputs, useTRPCClient } from '~/utils/trpc'

type DroughtIncidentNotificationFormProps = {
  site: SiteSchema
}

export const DroughtIncidentSection = ({ site }: DroughtIncidentNotificationFormProps) => {
  const { t } = useTranslation('sites')
  const isFeatureEnabled = useFeatureEnabled('drought_incidents_settings')
  const trpc = useTRPCClient()

  const [enabled, setEnabled] = useState(site.droughtIncidentEnabled ?? false)
  const queryClient = useQueryClient()

  const [open, setOpen] = useState(false)

  const subscriberState = useTopicSubscribersState({
    id: site.id,
    scope: 'site',
    notificationTrigger: 'new-drought'
  })

  const updateMembersSubscriptions = async () => {
    const subscriptionChanges: TRPCInputs['orgMembers']['updateMemberTopics'][] = subscriberState.operations.map(
      (op) => ({
        scope: 'site',
        scopeId: site.id,
        memberId: op.memberID,
        topics: ['new-drought', 'drought-updated', 'drought-resolved'].map(
          (notificationTrigger) =>
            ({
              action: op.operation,
              notificationTrigger
            }) satisfies TRPCInputs['orgMembers']['updateMemberTopics']['topics'][number]
        )
      })
    )

    try {
      await Promise.all(subscriptionChanges.map((change) => trpc.orgMembers.updateMemberTopics.mutate(change)))
    } catch (error) {
      console.error('Error updating member subscriptions:', error)
      throw error
    }
  }
  const { mutate: saveDroughtIncidentSettings, isPending } = useMutation({
    mutationFn: async () => {
      await updateSite({ ...site, droughtIncidentEnabled: enabled })
      await updateMembersSubscriptions()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          return queryKey[0] === 'sites' || queryKey[1] === site.id
        }
      })
      toast.success(t('sites.droughtIncident.updateSuccess'))
    },
    onError: (error) => {
      console.error('Error updating site', error)
      toast.error(t('sites.droughtIncident.updateError'))
    }
  })

  if (!isFeatureEnabled) {
    return null
  }

  const hasChange = enabled !== site.droughtIncidentEnabled || subscriberState.isDirty

  return (
    <SectionCard title={t('sites.droughtIncident.title')}>
      <Separator size="4" my="2" />
      <Collapsible.Root open={open} onOpenChange={setOpen}>
        <Flex direction="column" gap="7" py="3">
          <Flex justify="between" align="center" pr="6">
            <Flex direction="column" gap="1">
              <Text size="3" weight="medium">
                {t('sites.droughtIncident.triggerTitle')}
              </Text>
              <Text size="2" color="gray">
                {t('sites.droughtIncident.description')}
              </Text>
            </Flex>
            <Flex align={'center'} gap={'2'}>
              <Text size={'2'} as="label" weight={'medium'}>
                {t(`stations.leakTriggers.${!enabled ? 'disabled' : 'enabled'}`)}
              </Text>

              <Switch size={'3'} checked={enabled} onCheckedChange={setEnabled} />
              <Collapsible.Trigger>
                <Button
                  type="button"
                  variant="ghost"
                  size={'3'}
                  ml="3"
                  color="gray"
                  className="bg-transparent cursor-pointer"
                >
                  <LuChevronRight size={25} className={cn('transition-all', { ['rotate-90']: open })} />
                </Button>
              </Collapsible.Trigger>
            </Flex>
          </Flex>

          <Collapsible.Content>
            <Flex direction="column" gap="4" className={!enabled ? '!text-gray-9' : ''}>
              <Flex direction="column" gap="1">
                <Text size={'2'} weight={'medium'}>
                  {t('sites.droughtIncident.notifications.title')}
                </Text>
                <Text size={'1'} color="gray">
                  {t('sites.droughtIncident.notifications.description')}
                </Text>

                <OrgMemberPicker
                  initialSelection={subscriberState.initialSubscribers}
                  onSelectionChange={subscriberState.setSubscribedMembers}
                  disabled={!enabled}
                />
              </Flex>
            </Flex>
          </Collapsible.Content>
          <Separator size="4" />
        </Flex>
      </Collapsible.Root>
      <Flex justify="end">
        <Button
          size={'3'}
          className="mt-4"
          disabled={!hasChange}
          loading={isPending}
          onClick={() => saveDroughtIncidentSettings()}
        >
          {t('sites.droughtIncident.saveButton')}
        </Button>
      </Flex>
    </SectionCard>
  )
}
